import { z } from 'zod';
import { rutSchema, emailSchema, phoneOptionalSchema } from '@/schemas/shared';

// Age validation constants (from backend)
const MIN_AGE = 16;
const MAX_AGE = 100;

// Helper to calculate age
const calculateAge = (birthDate: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Tab 1: Personal Data Schema
export const createEmployeePersonalDataSchema = z.object({
  rutComplete: rutSchema,
  
  firstNames: z
    .string()
    .min(1, 'El nombre es requerido')
    .max(100, 'El nombre no puede exceder 100 caracteres'),
  
  lastName: z
    .string()
    .min(1, 'El apellido paterno es requerido')
    .max(100, 'El apellido paterno no puede exceder 100 caracteres'),
  
  secondLastName: z
    .union([z.string().max(100, 'El apellido materno no puede exceder 100 caracteres'), z.null()])
    .default(null)
    .transform((value) => value ?? ''),
  
  birthDate: z
    .string({
      required_error: 'La fecha de nacimiento es requerida',
      invalid_type_error: 'La fecha de nacimiento debe ser una fecha válida',
    })
    .min(1, 'La fecha de nacimiento es requerida')
    .refine(
      (value) => /^\d{4}-\d{2}-\d{2}$/.test(value),
      { message: 'Formato de fecha inválido (debe ser yyyy-mm-dd)' },
    )
    .refine(
      (value) => {
        const dateObj = new Date(value + 'T00:00:00');
        return !isNaN(dateObj.getTime());
      },
      { message: 'La fecha de nacimiento no es válida' },
    )
    .refine(
      (value) => {
        const dateObj = new Date(value + 'T00:00:00');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return dateObj <= today;
      },
      { message: 'La fecha de nacimiento no puede ser en el futuro' },
    )
    .refine(
      (value) => {
        const dateObj = new Date(value + 'T00:00:00');
        const age = calculateAge(dateObj);
        return age >= MIN_AGE;
      },
      { message: `El colaborador debe tener al menos ${MIN_AGE} años` },
    )
    .refine(
      (value) => {
        const dateObj = new Date(value + 'T00:00:00');
        const age = calculateAge(dateObj);
        return age <= MAX_AGE;
      },
      { message: `El colaborador no puede tener más de ${MAX_AGE} años` },
    ),
  
  genderCode: z
    .string({
      required_error: 'El sexo es requerido',
      invalid_type_error: 'El sexo debe ser un valor válido',
    })
    .min(1, 'El sexo es requerido')
    .refine(
      (value) => /^[MF]$/.test(value),
      { message: 'El sexo debe ser M o F' },
    ),
  
  maritalStatusId: z
    .number({
      required_error: 'El estado civil es requerido',
      invalid_type_error: 'El estado civil debe ser un número',
    })
    .int('El estado civil debe ser un número entero')
    .positive('El estado civil debe ser un valor válido'),
  
  employmentStatus: z
    .string({
      required_error: 'El estado del empleado es requerido',
      invalid_type_error: 'El estado del empleado debe ser un valor válido',
    })
    .refine(
      (value) => /^(V|N)$/.test(value),
      { message: 'El estado del empleado debe ser V o N' },
    ),

  doesNotSync: z.boolean().default(false),
});

// Tab 2: Contact Data Schema
export const createEmployeeContactDataSchema = z.object({
  corporateEmail: emailSchema,
  
  personalEmail: emailSchema,
  
  companyPhoneNumber: phoneOptionalSchema,
  
  personalPhoneNumber: phoneOptionalSchema,
});

// Tab 3: Address Data Schema
export const createEmployeeAddressDataSchema = z.object({
  address: z
    .union([z.string().max(200, 'La dirección no puede exceder 200 caracteres'), z.null()])
    .optional()
    .default(null)
    .transform((value) => value ?? ''),
  
  regionId: z
    .union([
      z.coerce.number().positive('La región debe ser un valor válido'),
      z.null(),
    ])
    .optional()
    .default(null),
  
  communeId: z
    .union([
      z.coerce.number().positive('La comuna debe ser un valor válido'),
      z.null(),
    ])
    .optional()
    .default(null),

  workAddress: z
    .union([z.string().max(500, 'La dirección laboral no puede exceder 500 caracteres'), z.null()])
    .optional()
    .default(null)
    .transform((value) => value ?? ''),

  workRegionId: z
    .union([
      z.coerce.number().positive('La región laboral debe ser un valor válido'),
      z.null(),
    ])
    .optional()
    .default(null),

  workCommuneId: z
    .union([
      z.coerce.number().positive('La comuna laboral debe ser un valor válido'),
      z.null(),
    ])
    .optional()
    .default(null),
});

// Combined schema for full form validation
export const createEmployeeFormSchema = z.object({
  ...createEmployeePersonalDataSchema.shape,
  ...createEmployeeContactDataSchema.shape,
  ...createEmployeeAddressDataSchema.shape,
});

// Type inference
export type CreateEmployeePersonalData = z.infer<typeof createEmployeePersonalDataSchema>;
export type CreateEmployeeContactData = z.infer<typeof createEmployeeContactDataSchema>;
export type CreateEmployeeAddressData = z.infer<typeof createEmployeeAddressDataSchema>;
export type CreateEmployeeFormValues = z.infer<typeof createEmployeeFormSchema>;
